<template>
    <div>

        <Card :header="$t('priceDeviations')" overlay :loading="loading">

            <template #nav>
                <div :key="spaceId" class="d-flex gap-3 flex-column">
                    <div class="d-flex gap-3">
                        <MultiselectAvailableCampaigns :disabled="loading" @changeTrackBy="campaigns = $event" @loading="campaignsLoading = $event" />
                        <Multiselect
                            v-if="showProductGroupsResults"
                            :fetch-options-function="fetchSpaceGroups"
                            :placeholder="$t('productGroup')"
                            :selected-options-label="$t('selectedGroups')"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="loading"
                            show-checkboxes
                            show-selected
                            multiple
                            preselect-all
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @changeTrackBy="groups = $event"
                            @loading="spaceGroupsLoading = $event"
                        />
                        <Multiselect
                            :fetch-options-function="fetchProducerProducts"
                            :placeholder="$t('findProduct')"
                            :selected-options-label="$t('selectedProducts')"
                            preserve-search
                            asynchronous
                            track-by="id"
                            label="name"
                            select-label=""
                            :disabled="loading && !spaceProductsLoading"
                            show-checkboxes
                            show-selected
                            multiple
                            :close-on-select="false"
                            :clear-on-select="false"
                            class="width-200"
                            @changeTrackBy="products = $event"
                            @loading="spaceProductsLoading = $event"
                        />
                        <FormCategoryTreeSelect
                            v-if="!showProductGroupsResults"
                            ref="FormCategoryTreeSelect"
                            :value="categories"
                            :disabled="loading"
                            class="width-200"
                            :clearable="false"
                            @change="categories = $event"
                            @loading="categoriesLoading = $event"
                        />
                    </div>
                    <div class="d-flex gap-3 justify-content-end align-items-center">
                        <b-form-checkbox v-model="showProductGroupsResults" switch :disabled="loading">{{ $t('showResultsForProductGroups') }}</b-form-checkbox>
                    </div>
                </div>
            </template>

            <ErrorMessage :value="requestData.getError()" />

            <div class="dropdown-divider border-bottom border-light"></div>

            <div class="d-flex flex-column">

                <DeviationsFromRecommendedPrice />

                <div class="dropdown-divider border-bottom border-light"></div>

                <DeviationsFromRecommendedPriceInTime />

            </div>

        </Card>

    </div>
</template>



<script>
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import DeviationsFromRecommendedPrice from './DeviationsFromRecommendedPrice.vue';
    import DeviationsFromRecommendedPriceInTime from './DeviationsFromRecommendedPriceInTime.vue';

    export default {
        name: 'PriceDeviations',
        components: {
            Multiselect,
            MultiselectAvailableCampaigns,
            FormCategoryTreeSelect,
            DeviationsFromRecommendedPrice,
            DeviationsFromRecommendedPriceInTime,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                requestData: this.$req('space/ProductsPriceChart'),
                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                requestSpaceGroups: this.$req('producerProductGroups/spaceGroups'),
                producerProduct: this.$req('space/ProducerProducts'),
                requestUpdatePrice: this.$req('producerProduct/product'),
                showProductGroupsResults: false,
                campaigns: [],
                groups: [],
                products: [],
                categories: [],
                campaignsLoading: false,
                spaceGroupsLoading: false,
                spaceProductsLoading: false,
                categoriesLoading: false,
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate } = this.timeRange || {};
                const requestParams = { startDate, endDate };
                if (this.products?.length) requestParams.products = this.products.join(',');
                if (this.campaigns?.length) requestParams.campaigns = this.campaigns.join(',');
                if (!this.showProductGroupsResults && this.categories?.length) requestParams.categories = this.categories.join(',');
                if (this.showProductGroupsResults && this.groups?.length) requestParams.groups = this.groups.join(',');
                return { urlParams, requestParams };
            },
            loadingFilters() {
                return this.campaignsLoading || this.spaceGroupsLoading || this.spaceProductsLoading || this.categoriesLoading;
            },
            loading() {
                return this.requestData.getLoading() || this.loadingFilters;
            },
            data() {
                return this.requestData.getData() || {};
            },
        },
        watch: {
            spaceId() {
                this.resetFilters();
            },
            apiParams: {
                immediate: false,
                handler(newVal, oldVal) {
                    if (JSON.stringify(newVal || {}) !== JSON.stringify(oldVal || {})) {
                        this.loadData();
                    }
                },
            },
        },
        methods: {
            loadData() {
                if (!this.loadingFilters) {
                    this.resetData();
                    this.fetchData();
                }
            },
            resetData() {
                this.requestData.abortAll();
                this.requestData.resetAll();
            },
            resetFilters() {
                this.campaigns = [];
                this.groups = [];
                this.products = [];
                this.categories = [];
            },
            fetchData() {
                this.requestData.get(this.apiParams);
            },
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id, v: 1 } });
            },
            fetchSpaceGroups() {
                return this.requestSpaceGroups.get({ urlParams: { spaceId: this.spaceId } }).then(data => data.items);
            },
            fetchProducerProducts({ search }) {
                return this.producerProduct.get({ urlParams: { spaceId: this.spaceId }, requestParams: { search } });
            },
        },
        created() {
            this.parentComponent.$on('resetFilters', this.resetFilters);
        },
        beforeDestroy() {
            this.parentComponent.$off('resetFilters', this.resetFilters);
        },
    };
</script>
